import React, { useEffect } from 'react';

import { Box, Typography } from '@mui/material';

import {
  RouterOutlet,
  useNavigate,
  useSelector,
  useTranslation,
} from 'third-party';

import { userPaymentListUrl } from 'constants/url';

import { appSelectors } from 'selectors';

const BriksioLogo = () => (
  <svg viewBox="0 0 500 400" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: '42px', width: 'auto', display: 'block' }}>
    <path d="M171.56 113.07L101.06 72.07L234.06 0.569962L308.56 42.07L171.56 113.07Z" fill="#FEE97C" stroke="#FFF1AC"/>
    <path d="M384.06 83.07L314.06 120.07L389.06 164.07L461.56 126.07L384.06 83.07Z" fill="#FEC401" stroke="#FFF1AC"/>
    <path d="M390.56 250.07L389.06 165.57L461.56 127.07V211.07L390.56 250.07Z" fill="#E36A01" stroke="#FFF1AC"/>
    <path d="M312.06 121.07L316.06 202.07L387.56 164.57L312.06 121.07Z" fill="#FC8600" stroke="#FFF1AC"/>
    <path d="M314.06 205.57L241.06 248.07L244.56 326.57L316.06 289.57L314.06 205.57Z" fill="#E97101" stroke="#FFF1AC"/>
    <path d="M242.56 326.07L240.06 248.07L69.56 152.07V226.07L242.56 326.07Z" fill="#FCA301" stroke="#FFF1AC"/>
    <path d="M98.56 131.57L68.56 150.07L240.06 246.07L314.56 203.57L238.06 159.07L173.06 193.57L98.56 154.57V131.57Z" fill="#FEC401" stroke="#FFF1AC"/>
    <path d="M309.56 42.57L172.56 114.07L174.06 193.07L311.56 120.57L309.56 42.57Z" fill="#F7AD00" stroke="#FFF1AC"/>
    <path d="M99.06 73.57L171.56 115.07L173.06 194.57L99.06 154.57V73.57Z" fill="#FED933" stroke="#FBE35B"/>
    <path d="M311.06 121.57L240.06 159.07L314.56 202.07L311.06 121.57Z" fill="#E36500" stroke="#FFF1AC"/>
    <path d="M388.06 165.57L315.56 204.57L317.56 288.57L389.56 250.57L388.06 165.57Z" fill="#C63F05" stroke="#FFF1AC"/>
    <ellipse cx="166.56" cy="50.57" rx="28.5" ry="16" fill="#FDF38E"/>
    <path d="M137.56 53.57C156.376 76.6195 193.779 64.7076 196.06 52.8077V50.57C196.208 51.31 196.204 52.0588 196.06 52.8077V71.57C182.56 97.57 137.56 82.57 137.56 72.07V53.57Z" fill="#F7C00D" stroke="#FFF1AC"/>
    <ellipse cx="235.06" cy="17.57" rx="28.5" ry="16" fill="#FDF38E"/>
    <path d="M206.06 20.57C224.876 43.6195 262.279 31.7076 264.56 19.8077V17.57C264.708 18.31 264.703 19.0588 264.56 19.8077V38.57C251.06 64.57 206.06 49.57 206.06 39.07V20.57Z" fill="#F7C00D" stroke="#FFF1AC"/>
  </svg>
);

export const AnonymsView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userInfoPending = useSelector(appSelectors.userInfoPending);
  const user = useSelector(appSelectors.user);

  useEffect(() => {
    if (!userInfoPending && user) {
      navigate(userPaymentListUrl());
    }
  }, [navigate, user, userInfoPending]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        flexGrow: 1,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Left branding panel */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          flexShrink: 0,
          width: '30%',
          padding: '40px 25px',
          background: 'radial-gradient(circle at top left, rgba(243, 156, 18, 0.08), transparent 50%), #32363F',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <BriksioLogo />
          <Typography
            sx={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#FFFFFF',
              letterSpacing: '-1px',
              lineHeight: 1,
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            Briksio
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: '#F9FAFB',
            opacity: 0.9,
            letterSpacing: '-0.3px',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          {t('common.slogan')}
        </Typography>
      </Box>

      {/* Right content panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          overflowY: 'auto',
          padding: { xs: '24px 16px', md: '40px 24px' },
        }}
      >
        <RouterOutlet />
      </Box>
    </Box>
  );
};
